import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClientInvoice } from '../../../redux/Thunk/InvoiceModule/InvoiceThunk';
import { Box, Typography, Button, Checkbox, IconButton, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GenericTable from '../../../Components/Ui_elements/GenericTable';
import AddIcon from '@mui/icons-material/Add';
import { invoiceWithSettings } from '../../../redux/Thunk/ReportModule/ReportThunk';
import { showSnackbar } from '../../../redux/Slice/snackbar/snackbarSlice';
import dayjs from 'dayjs';
import { Grid } from '@mui/material';
import Btn from '../../../Components/Ui_elements/Btn';
import { dotToComma, localizeNumber } from '../../../Helper/generalhelper';
import { useHandleSmartNavigation } from '../../../Helper/handleSmartNavigation';

const InvoiceCreatedList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { client_invoice_creation } = useSelector((state) => state.root.InvoiceModule);
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(1000);
    const [clientSortDir, setClientSortDir] = useState('asc'); // 'asc' | 'desc' for client group sorting
    const handleSmartNavigation = useHandleSmartNavigation();

    useEffect(() => {
        dispatch(getClientInvoice(
            {
                page,
                length: pageSize,
            }))
    }, [dispatch, page, pageSize])

    // Transform data for the GenericTable (sorted by client group name)
    const { tableData, groupTotals, grandTotal } = useMemo(() => {
        const totals = {};
        let overallTotal = 0;

        const rawData = client_invoice_creation?.data || {};

        const toItems = (groupObj) =>
            Object.entries(groupObj || {})
                .filter(([key]) => !isNaN(key))
                .map(([_, item]) => item);

        // Build groups list first (including 'general' as normal group)
        const groups = Object.entries(rawData)
            .map(([clientId, clientData]) => {
                const items = toItems(clientData);
                if (!items.length) return null;

                const groupTotal =
                    parseFloat(clientData?.totalAmount) ||
                    items.reduce((sum, item) => sum + parseFloat(item?.totalAmount || 0), 0);

                const clientName = clientId === 'general' ? 'General' : (items?.[0]?.contactName || '');

                totals[clientId] = groupTotal;
                overallTotal += groupTotal;

                return {
                    clientId,
                    clientName,
                    groupTotal,
                    items,
                };
            })
            .filter(Boolean);

        // Sort groups by clientName (case-insensitive)
        groups.sort((a, b) => {
            const left = (a.clientName || '').toString();
            const right = (b.clientName || '').toString();
            const cmp = left.localeCompare(right, undefined, { sensitivity: 'base' });
            return clientSortDir === 'desc' ? -cmp : cmp;
        });

        // Flatten back into table rows (header -> items -> footer) + grand total
        const data = [];
        groups.forEach(group => {
            data.push({
                isGroupHeader: true,
                clientId: group.clientId,
                clientName: group.clientName,
                groupTotal: group.groupTotal,
                rowCount: group.items.length,
            });

            group.items.forEach(item => {
                data.push({
                    ...item,
                    isGroupHeader: false,
                    clientId: group.clientId,
                    itemId: item.itemId,
                });
            });

            data.push({
                isGroupFooter: true,
                clientId: group.clientId,
                clientName: group.clientName,
                groupTotal: group.groupTotal,
            });
        });

        if (data.length > 0) {
            data.push({
                isGrandTotal: true,
                grandTotal: overallTotal,
            });
        }

        return { tableData: data, groupTotals: totals, grandTotal: overallTotal };
    }, [client_invoice_creation?.data, clientSortDir]);

    const toggleRowSelection = useCallback((id, clientId) => {
        setSelectedIds(prev => {
            // Get the clientId of the item being toggled
            const newItemClientId = clientId;

            // Check if we already have selected items from different groups
            const existingClientIds = new Set();
            tableData.forEach(row => {
                if (!row.isGroupHeader && !row.isGrandTotal && prev.includes(row.itemId)) {
                    existingClientIds.add(row.clientId);
                }
            });

            // If we already have selections from a different group
            if (existingClientIds.size > 0 && !existingClientIds.has(newItemClientId)) {
                dispatch(
                    showSnackbar({
                        message: 'You cannot select items from different client groups',
                        type: "error",
                    })
                );
                return prev; // Return previous selection without adding the new item
            }

            // Normal selection logic
            return prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id];
        });
    }, [tableData, dispatch]);

    const toggleGroupSelection = useCallback((clientId) => {
        const allRowIdsInGroup = tableData
            .filter(row => !row.isGroupHeader && !row.isGrandTotal && row.clientId === clientId)
            .map(row => row.itemId);

        setSelectedIds(prev => {
            const existingClientIds = new Set();
            tableData.forEach(row => {
                if (!row.isGroupHeader && !row.isGrandTotal && prev.includes(row.itemId)) {
                    existingClientIds.add(row.clientId);
                }
            });

            // If user is selecting a new group and already selected from another
            if (existingClientIds.size > 0 && !existingClientIds.has(clientId)) {
                dispatch(
                    showSnackbar({
                        message: 'You cannot select items from different client groups',
                        type: "error",
                    })
                );
                return prev;
            }

            const allSelected = allRowIdsInGroup.every(id => prev.includes(id));
            if (allSelected) {
                return prev.filter(id => !allRowIdsInGroup.includes(id));
            } else {
                return [...new Set([...prev, ...allRowIdsInGroup])];
            }
        });
    }, [tableData, dispatch]);


    // Calculate selected total per group
    const groupSelectedTotals = useMemo(() => {
        const totals = {};
        tableData.forEach(row => {
            if (!row.isGroupHeader && !row.isGrandTotal && selectedIds.includes(row.itemId)) {
                if (!totals[row.clientId]) totals[row.clientId] = 0;
                totals[row.clientId] += parseFloat(row.totalAmount || 0);
            }
        });
        return totals;
    }, [selectedIds, tableData]);

    const groupsWithSelectedItems = useMemo(() => {
        const groups = new Set();
        tableData.forEach(row => {
            if (!row.isGroupHeader && !row.isGrandTotal && selectedIds.includes(row.itemId)) {
                groups.add(row.clientId);
            }
        });
        return groups;
    }, [selectedIds, tableData]);

    const handleCreateInvoice = useCallback(() => {
        // Check if no items are selected
        if (selectedIds.length === 0) {
            dispatch(
                showSnackbar({
                    message: 'Please select at least one item to create an invoice',
                    type: "info",
                })
            );
            return;
        }

        // Get all clientIds for the selected items
        const selectedClientIds = new Set();
        tableData.forEach(row => {
            if (!row.isGroupHeader && !row.isGrandTotal && selectedIds.includes(row.itemId)) {
                selectedClientIds.add(row.clientId);
            }
        });

        // Check if items from multiple groups are selected
        if (selectedClientIds.size > 1) {
            dispatch(
                showSnackbar({
                    message: 'You cannot add two different client invoice',
                    type: "info",
                })
            );
            return;

        }

        // Format the selected IDs into the required payload structure
        const payload = {
            id: selectedIds.map(id => ({ id }))
        };

        // Dispatch the action with the formatted payload
        dispatch(invoiceWithSettings(payload))
            .then((response) => {
                navigate('/viewinvoicenew');
            })
            .catch((error) => {
                dispatch(
                    showSnackbar({
                        message: 'Invoice creation failed',
                        type: "error",
                    })
                );
                return;
            });

    }, [selectedIds, tableData, dispatch, navigate]);

    const columns = [
        {
            name: '#',
            label: '',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];

                    if (rowData.isGroupHeader) {
                        const allSelected = tableData
                            .filter(row => !row.isGroupHeader && !row.isGrandTotal && row.clientId === rowData.clientId)
                            .every(row => selectedIds.includes(row.itemId));

                        const someSelected = tableData
                            .filter(row => !row.isGroupHeader && !row.isGrandTotal && row.clientId === rowData.clientId)
                            .some(row => selectedIds.includes(row.itemId)) && !allSelected;

                        return (
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={() => toggleGroupSelection(rowData.clientId)}
                            />
                        );
                    }

                    if (rowData.isGrandTotal || rowData.isGroupFooter) {
                        return null;
                    }

                    return (
                        <Checkbox
                            checked={selectedIds.includes(rowData.itemId)}
                            onChange={() => toggleRowSelection(rowData.itemId, rowData.clientId)}
                        />
                    );
                },
                setCellProps: () => ({ width: 50 }),
                setCellHeaderProps: () => ({
                    style: { paddingLeft: '23px' }
                }),
            }
        },
        {
            name: 'orderNumber',
            label: 'Scoop number',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    // Group Header Render
                    if (rowData.isGroupHeader) {
                        const hasSelectedItems = groupsWithSelectedItems.has(rowData.clientId);
                        return (
                            <Box display="flex" alignItems="center" gap={1}>
                                <span style={{ fontWeight: 'bold'}}>Client: {rowData.clientName}</span>
                            </Box>
                        );
                    }
                    
                    if (rowData.isGroupFooter) {
                        const selectedTotal = groupSelectedTotals[rowData.clientId] || 0;
                        return (
                            <span style={{ fontWeight: 'bold'}}>
                                Selected Total: {dotToComma(selectedTotal.toFixed(2))}
                            </span>
                        );
                    }

                    if (rowData.isGrandTotal) return null; // No need to show 'Grand Total';
                    
                    // Normal Order Number with onClick to get orderId
                    return (
                        <a
                            href={`/general/scoop/${rowData.orderId}`}
                            style={{ cursor: 'pointer', color: 'blue', textDecoration: 'none' }}
                            >
                            {`${rowData.orderNumber}-${String(rowData.item_number).padStart(3, '0')}` || 'N/A'}
                        </a>
                    );
                },
                setCellHeaderProps: () => ({ style: { width: '130px' } }),
            }
        },
        {
            name: 'contactName',
            label: 'Client',
            options: {
                sort: true,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    // For group header, render Client name + Invoice button
                    if (rowData.isGroupHeader) {
                        const hasSelectedItems = groupsWithSelectedItems.has(rowData.clientId);
                        return (
                            <Box display="flex" alignItems="center" gap={1}>
                                {/* <span style={{ fontWeight: 'bold'}}>Client: {rowData.clientName}</span> */}
                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        '&:hover': {
                                            cursor: !hasSelectedItems ? 'not-allowed' : 'pointer'
                                        }
                                    }}
                                >
                                    <Btn
                                        size="medium"
                                        color="primary"
                                        onClick={() => handleCreateInvoiceForGroup(rowData.clientId)}
                                        sx={{
                                            borderRadius: 1,
                                            display: 'flex',
                                            padding: "2px 5px", 
                                            alignItems: 'center',
                                            '&.Mui-disabled': {
                                                backgroundColor: '#502DC2',
                                                opacity: 0.6,
                                            },
                                        }}
                                        disabled={!hasSelectedItems}
                                    >
                                        <AddIcon sx={{ fontSize: "16px" }} />
                                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                                            Invoice
                                        </Typography>
                                    </Btn>
                                </Box>
                            </Box>
                        );
                    }
                    if (rowData.isGrandTotal || rowData.isGroupFooter) return null;
                    // Normal row render
                    return (
                        <a
                            href={`/viewclient/${rowData?.iClientId}`}
                            style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1976d2' }}
                        >
                            {rowData.contactName}
                        </a>
                    );
                },
                setCellHeaderProps: () => ({ style: { width: '160px' } }),
            }
        },
        {
            name: 'itemPonumber',
            label: 'PO number',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    if (rowData.isGroupHeader && tableMeta.rowIndex !==0) {
                        return <strong>PO number</strong>;
                    }
                    if (rowData.isGroupFooter || rowData.isGrandTotal) {
                        return null;
                    }
                    return rowData.itemPonumber || '';
                },
                setCellHeaderProps: () => ({ style: { width: '160px' } }),
            }
        },
        {
            name: 'itemDuedate',
            label: 'Due Date',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    if (rowData.isGroupHeader && tableMeta.rowIndex !==0) {
                        return <strong>Due Date</strong>;
                    }
                    if (rowData.isGroupHeader || rowData.isGrandTotal || rowData.isGroupFooter) return null;
                    return dayjs(rowData.itemDuedate).format('MM.DD.YYYY') || 'N/A';
                },
                setCellHeaderProps: () => ({ style: { width: '100px', } }),
            }
        },
        {
            name: 'itemStatus',
            label: 'Status',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    if (rowData.isGroupHeader && tableMeta.rowIndex !==0) {
                        return <strong>Status</strong>;
                    }
                    if (rowData.isGroupHeader || rowData.isGrandTotal || rowData.isGroupFooter) return null;
                    return (
                        <Box display="flex" alignItems="center">
                            <span style={{
                                color: rowData.itemStatus === 'Approved' ? 'green' : 'orange',
                                marginRight: '8px'
                            }}>
                                ●
                            </span>
                            {rowData.itemStatus}
                        </Box>
                    );
                },
                setCellHeaderProps: () => ({ style: { width: '100px', } }),
            }
        },
        {
            name: 'totalAmount',
            label: 'Price',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];

                    if (rowData.isGroupHeader && tableMeta.rowIndex !==0) {
                        return <strong>Price</strong>;
                    }
                    //if (rowData.isGroupHeader) {
                    if (rowData.isGroupFooter) {
                        return (
                            <div style={{ padding: '8px 0', fontWeight: 'bold' }}>
                                Total: {dotToComma(Number(rowData.groupTotal || 0).toFixed(2))}
                            </div>
                        );
                    }

                    if (rowData.isGrandTotal || rowData.isGroupFooter || rowData.isGroupHeader) return null;

                    const amount = Number(rowData.totalAmount);
                    const safeAmount = isNaN(amount) ? 0 : amount;

                    return localizeNumber(safeAmount.toFixed(2));
                },
                setCellHeaderProps: () => ({ style: { width: '100px', } }),
            }
        },
        {
            name: 'client_currency',
            label: 'Currency',
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = tableData[tableMeta.rowIndex];
                    if (rowData.isGroupHeader && tableMeta.rowIndex !==0) {
                        return <strong>Currency</strong>;
                    }
                    if (rowData.isGroupHeader || rowData.isGrandTotal || rowData.isGroupFooter) return null;
                    const currencyString = rowData.client_currency;
                    const currencyCode = currencyString?.split(',')[0];
                    return currencyCode || 'N/A';
                },
                setCellHeaderProps: () => ({ style: { width: '70px', } }),
            }
        }
    ];
    const handleCreateInvoiceForGroup = useCallback((clientId) => {
        if (selectedIds.length === 0) {
            dispatch(
                showSnackbar({
                    message: 'Please select at least one item to create an invoice',
                    type: "info",
                })
            );
            return;
        }
        const selectedItemsInGroup = tableData
            .filter(row =>
                !row.isGroupHeader &&
                !row.isGrandTotal &&
                row.clientId === clientId &&
                selectedIds.includes(row.itemId)
            )
            .map(row => row.itemId);

        if (selectedItemsInGroup.length === 0) return;

        const payload = {
            id: selectedItemsInGroup.map(id => ({ id }))
        };

        dispatch(invoiceWithSettings(payload))
            .then(() => {
                navigate('/viewinvoicenew');
            })
            .catch((error) => {
                console.error("Invoice creation failed", error);
                dispatch(
                    showSnackbar({
                        message: 'Invoice creation failed',
                        type: "error",
                    })
                );
            });
    }, [tableData, selectedIds, dispatch, navigate]);

    const options = useMemo(() => ({
        selectableRows: "none",
        serverSide: true,
        count: client_invoice_creation?.totalRecode || 0,
        page,
        rowsPerPage: pageSize,
        rowsPerPageOptions: [100, 200, 300,400, 500, 1000],
        search: false,
        download: false,
        print: false,
        filter: false,
        viewColumns: false,
        sort: true,
        sortOrder: {
            name: 'contactName',
            direction: clientSortDir,
        },
        onTableChange: (action, tableState) => {
            switch (action) {
                case "changePage":
                    setPage(tableState.page);
                    break;
                case "changeRowsPerPage":
                    setPageSize(tableState.rowsPerPage);
                    setPage(0);
                    break;
                case "sort": {
                    // Only sort by Client column (index 2), keep grouped layout intact
                    if (tableState?.activeColumn === 2) {
                        const nextDir = tableState?.sortOrder?.direction || 'asc';
                        setClientSortDir(nextDir);
                    }
                    break;
                }
                default:
                    break;
            }
        },
        // ✅ Add this block
        setRowProps: (row, dataIndex) => {
            const currentRow = tableData[dataIndex];
            const nextRow = tableData[dataIndex + 1];
            // Group Footer Row
            if (currentRow?.isGroupFooter) {
                return {
                    className: "group-footer-row",
                    style: {
                        backgroundColor: "#f2f7ff",
                        fontStyle: "italic",
                        borderBottom: "3px solid #ffffff",  // white separator
                    }
                };
            }
            // Separator between client groups
            if (
                currentRow &&
                nextRow &&
                currentRow.clientId !== nextRow.clientId
            ) {
                return {
                    style: {
                        borderBottom: "3px solid #ffffff", // white line
                    }
                };
            }
            //Group Header Row
            if (currentRow?.isGroupHeader) {
                return {
                    className: "row-group-header",
                    style: {
                        backgroundColor: "#f9f9f9",
                        fontWeight: "bold",
                        borderBottom: "2px solid #ffffff" // header separator
                    }
                };
            }
            // Default row styling
            return {
                style: {
                    borderBottom: "2px solid #ffffff", // white separator for every row
                }
            };
        },


    }), [client_invoice_creation?.totalRecode, page, pageSize, clientSortDir]);

    return (
        <Box className="table-container-global">
            <Typography variant="h5" mb={2} sx={{ fontWeight: 600 }}>
                Invoice Create
            </Typography>
            <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                <Grid item>
                    <Btn variant="contained" onClick={(e) => handleSmartNavigation(e, "/client/invoice")}>
                        Go To Invoice List
                    </Btn>
                </Grid>

                <Grid item>
                    <Btn
                    variant="contained"
                    onClick={() => handleCreateInvoice()}
                    disabled={selectedIds.length === 0}
                    >
                    Create Invoice ({selectedIds.length})
                    </Btn>
                </Grid>
            </Grid>

            <Box className="invoice-created-table" >
                <GenericTable
                columns={columns}
                data={tableData}
                showAction={false}
                options={options}
            />
            </Box>
        </Box>
    );
};

export default InvoiceCreatedList;